import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import GuidePageNotFound from '../errorPages/GuidePageNotFound';
import InternalServerError from '../errorPages/InternalServerError';
import PageHeader from './PageHeader';
import PageTableOfContents from './PageTableOfContents';
import Forbidden from '../errorPages/Forbidden';
import { useActivePage, useFetchPageById } from '../../queries/guides';
import SkeletonPage from './SkeletonPage';
import SectionList from '../sections/SectionList';
import BottomNavigation from '../guides/BottomNavigation';
import { GAtrackNavigation } from '../../helpers/googleAnalytics';
import { useGuideStore } from '../../stores/guideStore';
import { isDevelopment } from '../../constants/app';
import {
  useTrackGuideNavigation,
  useTrackPageNavigation,
} from '../guides/hooks';
import { GuideType, Page as PageType } from '../../types';
import EmptyPage from './EmptyPage';
import { useThemesetByGuideId } from '../../queries/themesets';
import { useSidebarStore } from '../../stores/sidebarStore';
import { extractTextWidgets } from '../../helpers/utils';

const Page: React.FC<{
  guideType: GuideType;
  activeGuideId: string;
  activePageId: string;
}> = ({ guideType, activeGuideId, activePageId }) => {
  const history = useHistory();
  const isEditMode = useGuideStore(s => s.isEditMode);

  const isMultiPage = guideType === GuideType.MULTI_PAGE;

  const {
    data: page,
    isLoading,
    isError,
    error,
  } = useFetchPageById(activeGuideId, activePageId);

  const isPageEmpty = page?.sections.every(section =>
    section.columns.every(column => column.widgets.length === 0),
  );

  useSetTableOfContents();
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.querySelector(`[id='${id}']`);

        if (element) {
          element.scrollIntoView();
          history.replace(pathname, { hash: '' });
        }
      }, 100);
    }
  }, [hash, page, pathname, history]);

  React.useEffect(() => {
    if (!isLoading && !isDevelopment) {
      GAtrackNavigation(page?.title);
    }
  }, [isLoading, page?.title]);

  if (isLoading) {
    return <SkeletonPage />;
  }
  if (!isEditMode && isPageEmpty) {
    return <EmptyPage canEdit={page?.canEdit} />;
  }

  if (isError) {
    switch (error) {
      case 403:
        return <Forbidden documentType="page" />;
      case 404:
        return <GuidePageNotFound />;
      case 500:
      default:
        return <InternalServerError />;
    }
  }

  return (
    <PageContent
      page={page}
      activeGuideId={activeGuideId}
      isMultiPage={isMultiPage}
    />
  );
};

export default Page;

const PageContent: React.FC<{
  isMultiPage: boolean;
  page: PageType;
  activeGuideId: string;
}> = ({ isMultiPage, page, activeGuideId }) => {
  useTrackPageNavigation();
  useTrackGuideNavigation();
  const activePageId = useGuideStore(s => s.activePageId);
  const { data: themeset } = useThemesetByGuideId(activeGuideId);

  if (!activePageId || !themeset) {
    return null;
  }

  return (
    <>
      {page.header.isVisible && isMultiPage && (
        <PageHeader activeGuideId={activeGuideId} page={page} />
      )}
      <PageTableOfContents />
      <SectionList
        sections={page.sections}
        isPageEditable={page.canEdit}
        defaultSectionBackgroundColor={themeset.pageSection.backgroundColor}
      />
      {isMultiPage && <BottomNavigation activeGuideId={activeGuideId} />}
    </>
  );
};

const useSetTableOfContents = () => {
  const { data: page } = useActivePage();
  const setTableOfContents = useSidebarStore(s => s.setTableOfContents);

  React.useEffect(() => {
    if (page) {
      const textWidgets = extractTextWidgets(page);
      setTableOfContents(textWidgets);
    }
  }, [page, setTableOfContents]);
};
